import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, CheckCircle, ArrowRight, ShieldCheck, TreePine, HelpCircle, ChevronDown } from 'lucide-react';
import { COMPANY_INFO, FALLBACK_PAGES, FALLBACK_GALLERIES } from '../data';
import GallerySection from '../components/GallerySection';

function formatArticleContent(rawContent) {
  if (!rawContent) return "";
  let html = (rawContent || "")
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'");

  // Group 2 or more consecutive <h3>...</h3><p>...</p> pairs into a 2-column feature card grid
  const uspBlockRegex = /((?:<h3\b[^>]*>(?:(?!<\/h3>)[\s\S])+<\/h3>\s*<p\b[^>]*>(?:(?!<\/p>)[\s\S])+<\/p>\s*){2,})/gi;
  html = html.replace(uspBlockRegex, (match) => {
    const pairRegex = /<h3\b[^>]*>((?:(?!<\/h3>)[\s\S])+?)<\/h3>\s*<p\b[^>]*>((?:(?!<\/p>)[\s\S])+?)<\/p>/gi;
    const cards = [];
    let pair;
    while ((pair = pairRegex.exec(match)) !== null) {
      const title = pair[1].trim();
      const desc = pair[2].trim();
      cards.push(`  <div class="bg-stone-900/60 border border-stone-800 p-6 rounded-xl">
    <h3 class="text-emerald-400 font-bold uppercase tracking-tight text-base mb-2">${title}</h3>
    <p class="text-sm text-stone-300">${desc}</p>
  </div>`);
    }
    if (cards.length < 2) return match;
    return `\n<div class="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">\n${cards.join('\n')}\n</div>\n`;
  });

  // Un-wrap single-image paragraphs: <p><img ...></p> -> <img ...>
  html = html.replace(/<p>\s*(<img\b[^>]*\/?>)\s*<\/p>/gi, '$1');

  // Group 2 or more consecutive <img> tags into a 2-column grid (2 images per row)
  html = html.replace(/((?:<img\b[^>]*\/?>\s*){2,})/gi, (match) => {
    const imgMatches = match.match(/<img\b[^>]*\/?>/gi);
    if (!imgMatches || imgMatches.length < 2) return match;

    const wrappedImages = imgMatches.map((imgTag) => {
      let cleanTag = imgTag;
      if (!cleanTag.includes('class=')) {
        cleanTag = cleanTag.replace('<img', '<img class="w-full h-44 sm:h-52 object-cover hover:scale-105 transition-transform duration-500"');
      } else {
        cleanTag = cleanTag.replace(/class="([^"]*)"/, 'class="$1 w-full h-44 sm:h-52 object-cover hover:scale-105 transition-transform duration-500"');
      }
      return `<div class="overflow-hidden rounded-xl border border-stone-800 bg-stone-900/40">${cleanTag}</div>`;
    }).join('\n');

    return `\n<div class="grid grid-cols-1 sm:grid-cols-2 gap-4 my-8 not-prose">\n${wrappedImages}\n</div>\n`;
  });

  return html;
}

export default function PageDetail() {
  const { category, slug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine the correct page slug based on routing
  const currentSlug = slug || category || location.pathname.split('/').pop() || 'forside';

  useEffect(() => {
    setLoading(true);
    
    // 1. Fetch Page Content from CMS
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then(res => res.json())
      .then(data => {
        // Find the page matching our page_slug
        const foundPage = data.find(p => p.page_slug === currentSlug);
        if (foundPage) {
          setPage(foundPage);
          updateMetadata(foundPage);
          if (foundPage.gallery_slug) {
            fetchGallery(foundPage.gallery_slug);
          } else {
            setGalleryImages([]);
          }
        } else {
          loadFallback();
        }
      })
      .catch(err => {
        console.warn("Kunne ikke hente side fra CMS, bruger fallback:", err);
        loadFallback();
      })
      .finally(() => {
        setLoading(false);
      });

    function loadFallback() {
      const fallback = FALLBACK_PAGES.find(p => p.page_slug === currentSlug);
      if (fallback) {
        setPage(fallback);
        updateMetadata(fallback);
        if (fallback.gallery_slug && FALLBACK_GALLERIES[fallback.gallery_slug]) {
          setGalleryImages(FALLBACK_GALLERIES[fallback.gallery_slug]);
        } else {
          setGalleryImages([]);
        }
      } else {
        setPage(null);
      }
    }

    // 2. Fetch Gallery Content from CMS
    function fetchGallery(gallerySlug) {
      fetch('https://api.dev.auxo.dk/public/content/gallery')
        .then(res => res.json())
        .then(data => {
          const foundGallery = data.find(g => g.gallery_slug === gallerySlug);
          if (foundGallery && foundGallery.images) {
            setGalleryImages(foundGallery.images);
          } else if (FALLBACK_GALLERIES[gallerySlug]) {
            setGalleryImages(FALLBACK_GALLERIES[gallerySlug]);
          }
        })
        .catch(err => {
          console.warn("Kunne ikke hente galleri fra CMS, bruger fallback:", err);
          if (FALLBACK_GALLERIES[gallerySlug]) {
            setGalleryImages(FALLBACK_GALLERIES[gallerySlug]);
          }
        });
    }

    // 3. Update Document Metadata
    function updateMetadata(p) {
      // Set Document Title
      const titleText = p.meta_title?.da || p.meta_title || `${p.title?.da || p.title} | ${COMPANY_INFO.name}`;
      document.title = titleText;

      // Set Meta Description
      const descText = p.meta_description?.da || p.meta_description || "Professionel maskinskovning, skovservice, naturpleje, flishugning, udkørsel samt stub- og rodfræsning i Nordjylland.";
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', descText);

      // Set Canonical Link
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      const canonicalHref = `https://www.vesthimmerlands-maskinskovning.dk${location.pathname}`;
      canonicalLink.setAttribute('href', canonicalHref);

      // Set Open Graph Title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', titleText);

      // Set Open Graph Description
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.setAttribute('content', descText);

      // Set Open Graph Image
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      // Set to empty string as requested for this page, or default/empty for others
      const imageText = p.og_image || "";
      ogImage.setAttribute('content', imageText);
    }
  }, [currentSlug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-stone-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-stone-400 text-sm font-semibold uppercase tracking-wider">Indlæser indhold...</p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-stone-950 text-center px-4">
        <TreePine className="w-16 h-16 text-stone-600 mb-4" />
        <h1 className="text-2xl font-black text-white uppercase tracking-tight">Siden blev ikke fundet</h1>
        <p className="text-stone-400 mt-2 max-w-md">Vi kunne ikke finde den ønskede side. Kontroller venligst URL'en eller gå tilbage til forsiden.</p>
        <Link to="/" className="mt-6 bg-[#348F57] hover:bg-[#2a7346] text-white px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide transition-all">
          Gå til forsiden
        </Link>
      </div>
    );
  }

  // Determine hero background image based on page
  const getHeroImage = () => {
    if (galleryImages && galleryImages.length > 0) {
      return galleryImages[0];
    }
    // Fallbacks
    if (currentSlug.includes('walking-floor')) {
      return "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/walking-floor-trailer-til-bulkgods.jpg";
    }
    if (currentSlug.includes('blokvogn')) {
      return "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/sikker-maskintransport.jpg";
    }
    if (currentSlug.includes('gravemaskine')) {
      return "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/gravemaskine-2_1.jpg";
    }
    if (currentSlug.includes('skovservice') || currentSlug.includes('maskinskovning') || currentSlug.includes('vognmand') || currentSlug.includes('flishugning')) {
      return "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/5ffdb8d7-ea0e-4910-b7f5-2153e40e7bd7.jpg";
    }
    return "https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/messenger-creation-ad909769-b7dd-4957-8045-222f0c1d3d66_1.jpeg";
  };

  return (
    <div className="bg-stone-950">
      {/* Page Hero */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden bg-stone-950">
        <div className="absolute inset-0 z-0">
          <img 
            src={getHeroImage()} 
            alt={page.title?.da || page.title} 
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-swamp-300 mb-2 block">
            Vesthimmerlands Maskinskovning
          </span>
          <p className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none">
            {page.title?.da || page.title}
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left: Rich Text Content */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-stone-900/40 border border-stone-900 p-8 sm:p-10 rounded-2xl">
              <article className="prose prose-invert prose-emerald max-w-none">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: formatArticleContent(page.content?.da || page.content || "")
                  }} 
                  className="text-stone-300 
                    [&>h1]:text-2xl [&>h1]:sm:text-3xl [&>h1]:font-black [&>h1]:text-white [&>h1]:uppercase [&>h1]:tracking-tight [&>h1]:mb-6 [&>h1]:mt-10 [&>h1]:border-b [&>h1]:border-stone-800 [&>h1]:pb-3
                    [&>h2]:text-xl [&>h2]:sm:text-2xl [&>h2]:font-bold [&>h2]:text-white [&>h2]:uppercase [&>h2]:tracking-tight [&>h2]:mb-4 [&>h2]:mt-8
                    [&>h3]:text-lg [&>h3]:font-bold [&>h3]:text-stone-200 [&>h3]:uppercase [&>h3]:tracking-tight [&>h3]:mb-3 [&>h3]:mt-6
                    [&>p]:text-sm [&>p]:sm:text-base [&>p]:leading-relaxed [&>p]:mb-6 [&>p]:text-stone-400
                    [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ul]:mb-6 [&>ul]:text-stone-400 [&>ul]:text-sm [&>ul]:sm:text-base
                    [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:space-y-2 [&>ol]:mb-6 [&>ol]:text-stone-400 [&>ol]:text-sm [&>ol]:sm:text-base
                    [&>li]:pl-1
                    [&>strong]:text-white [&>strong]:font-semibold
                    [&_a]:text-emerald-400 [&_a]:underline [&_a:hover]:text-emerald-300 [&_a]:transition-colors [&_a]:font-medium
                    [&_a[class*='bg-']]:!text-white [&_a[class*='bg-']]:!no-underline [&_a[class*='bg-']]:hover:!text-white
                  "
                />
              </article>
            </div>

            {/* FAQ Section for Skovservice */}
            {currentSlug === 'skovservice' && (
              <SkovserviceFAQ />
            )}

            {/* FAQ Section for Vognmand */}
            {currentSlug === 'vognmand' && (
              <VognmandFAQ />
            )}

            {/* FAQ Section for Stub- og rodfræsning */}
            {currentSlug === 'stub-og-rodfraesning' && (
              <StubOgRodfraesningFAQ />
            )}

            {/* Render Gallery Section if page has a gallery reference */}
            {page.gallery_slug && galleryImages.length > 0 && (
              <GallerySection 
                images={galleryImages} 
                title={
                  currentSlug === 'skovservice' 
                    ? "Billeder fra skovservice i Midt- og Nordjylland" 
                    : `Billeder fra ${page.title?.da || page.title}`
                } 
              />
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="space-y-8">
            {/* Quick Contact Box */}
            <div className="bg-stone-900 border border-stone-800 p-8 rounded-2xl space-y-6 shadow-xl">
              <h3 className="text-lg font-bold text-white uppercase tracking-tight border-b border-stone-800 pb-3">
                Få et uforpligtende tilbud
              </h3>
              <p className="text-sm text-stone-300 leading-relaxed">
                Har du spørgsmål til vores ydelser, eller ønsker du et skræddersyet tilbud på din opgave? Kontakt os i dag, og lad os tage en snak.
              </p>
              <div className="space-y-4 pt-2">
                <a 
                  href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                  className="flex items-center gap-3.5 p-3.5 rounded-xl bg-stone-950 border border-stone-800 hover:border-swamp-500 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-swamp-900/90 border border-swamp-700 flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-swamp-300 group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <span className="block text-xs text-swamp-300 uppercase font-bold">Ring direkte</span>
                    <span className="block text-sm font-bold text-white whitespace-nowrap">{COMPANY_INFO.phone}</span>
                  </div>
                </a>
                <a 
                  href={`mailto:${COMPANY_INFO.email}`} 
                  className="flex items-center gap-3.5 p-3.5 rounded-xl bg-stone-950 border border-stone-800 hover:border-swamp-500 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-swamp-900/90 border border-swamp-700 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-swamp-300 group-hover:scale-110 transition-transform" />
                  </div>
                  <div>
                    <span className="block text-xs text-stone-400 uppercase font-bold">Send en e-mail</span>
                    <span className="block text-sm font-bold text-white whitespace-nowrap">{COMPANY_INFO.email}</span>
                  </div>
                </a>
              </div>
              <Link 
                to="/kontakt" 
                className="w-full bg-swamp-900 hover:bg-swamp-800 text-white py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all shadow-lg shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Gå til kontaktformular</span>
                <ArrowRight className="w-4 h-4 text-swamp-300 stroke-[2.5]" />
              </Link>
            </div>

            {/* Certification & Safety Box */}
            <div className="bg-stone-900/70 border border-stone-800 p-8 rounded-2xl space-y-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-swamp-900/90 border border-swamp-700">
                  <ShieldCheck className="w-5 h-5 text-swamp-300 shrink-0" />
                </div>
                <h4 className="font-black text-white uppercase tracking-tight text-sm">PEFC-Godkendt</h4>
              </div>
              <p className="text-xs text-stone-300 leading-relaxed">
                Vi er godkendt til at arbejde i PEFC-certificerede skove. Det er din garanti for, at alt skovningsarbejde udføres i fuld overensstemmelse med gældende standarder for bæredygtig skovdrift.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <div className="p-2 rounded-lg bg-swamp-900/90 border border-swamp-700">
                  <CheckCircle className="w-5 h-5 text-swamp-300 shrink-0" />
                </div>
                <span className="text-xs font-black text-white uppercase tracking-wide">Miljøvenlig bioolie</span>
              </div>
              <p className="text-xs text-stone-300 leading-relaxed">
                Vores maskiner anvender udelukkende biologisk nedbrydelig bioolie på hydraulikanlæggene samt vakuumanlæg for at beskytte skovbunden og vandløb mod forurening.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SkovserviceFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = [
    {
      question: "Hvornår giver det mening at få hjælp til skovservice?",
      answer: "Skovservice giver mening, når et skovområde, læhegn eller naturareal kræver mere end almindelig manuel pleje. Det kan være ved udtynding, oprydning, fældning, grenknusning eller maskinelt arbejde i vanskeligt terræn. For at få arbejdet udført sikkert og effektivt kan det være en fordel at få en professionel skoventreprenør til at vurdere opgaven."
    },
    {
      question: "Hvad kan skovservice typisk omfatte?",
      answer: "Skovservice kan dække over flere typer opgaver, alt efter arealets størrelse, tilstand og formål. Det kan blandt andet være maskinskovning, udkørsel af træ, flishugning, naturpleje, hegnsklipning og oprydning efter fældning. En faglig vurdering kan hjælpe med at vælge den løsning, der passer bedst til både drift, adgangsforhold og fremtidig vedligeholdelse."
    },
    {
      question: "Kan man selv stå for mindre skovarbejde?",
      answer: "Mindre opgaver som let beskæring, oprydning og fjernelse af smågrene kan man ofte klare selv, hvis forholdene er sikre. Ved større træer, tunge materialer, ujævnt terræn eller maskinelle opgaver er risikoen dog væsentligt højere. Her kan professionel hjælp sikre, at arbejdet bliver udført korrekt og uden unødige skader på personer, træer eller arealer."
    },
    {
      question: "Hvad er forskellen på skovservice og maskinskovning?",
      answer: "Maskinskovning handler især om selve fældningen og håndteringen af træ med skovmaskiner. Skovservice er bredere og kan også omfatte vedligeholdelse, naturpleje, udkørsel, flishugning og efterfølgende oprydning. Ved større opgaver kombineres ydelserne ofte, så skoven eller arealet bliver håndteret som en samlet løsning."
    },
    {
      question: "Hvordan vurderes prisen på skovservice?",
      answer: "Prisen afhænger typisk af opgavens omfang, terræn, adgangsforhold, mængden af træ og det nødvendige maskinel. Der kan også være forskel på, om der kun skal udføres en enkelt opgave, eller om arealet kræver flere arbejdsgange. Den mest præcise pris fås normalt efter en konkret vurdering af området og det arbejde, der skal udføres."
    },
    {
      question: "Kan skovservice udføres i både Nordjylland og Midtjylland?",
      answer: "Mange skoventreprenører arbejder på tværs af større geografiske områder, især når opgaven kræver specialmaskiner. Skovservice i Nordjylland, Midtjylland og Jylland kan derfor ofte planlægges efter opgavens størrelse og praktiske forhold. Ved større eller tilbagevendende opgaver kan det være relevant at få lavet en samlet plan for både kørsel, maskiner og udførelse."
    },
    {
      question: "Hvad bør man have klar, før man kontakter en skoventreprenør?",
      answer: "Det er en fordel at kende arealets placering, opgavens type og om der er særlige adgangsforhold eller hensyn. Billeder, kort eller en kort beskrivelse af området kan gøre det lettere at vurdere, hvilket maskinel der skal bruges. Ved tvivl kan en fagperson gennemgå opgaven og foreslå den mest praktiske løsning fra start."
    }
  ];

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-stone-900/40 border border-stone-900 p-8 sm:p-10 rounded-2xl space-y-6 mt-8">
      <div className="flex items-center gap-3 border-b border-stone-800 pb-4">
        <HelpCircle className="w-6 h-6 text-emerald-500 shrink-0" />
        <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-tight">
          Ofte stillede spørgsmål om skovservice
        </h2>
      </div>
      <div className="divide-y divide-stone-800/60">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <button
                onClick={() => toggleIndex(index)}
                className="w-full flex justify-between items-start gap-4 text-left text-white font-bold hover:text-emerald-400 transition-colors py-2 group"
                aria-expanded={isOpen}
              >
                <span className="text-sm sm:text-base tracking-tight leading-snug">
                  {item.question}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-stone-500 group-hover:text-emerald-400 shrink-0 transition-transform duration-300 mt-0.5 ${
                    isOpen ? 'rotate-180 text-emerald-500' : ''
                  }`} 
                />
              </button>
              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="text-stone-400 text-sm sm:text-base leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VognmandFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = [
    {
      question: "Hvilke opgaver kan en vognmand typisk hjælpe med?",
      answer: "En vognmand kan hjælpe med transport af materialer, maskiner, træ, flis og andet gods, der kræver det rette udstyr. Ved skov- og naturplejeopgaver kan transporten også handle om udkørsel af træ eller flytning af maskiner mellem arbejdssteder. Hvis opgaven kræver tung transport, særlige adgangsforhold eller planlægning, bør den vurderes af en professionel vognmand."
    },
    {
      question: "Hvornår er blokvognstransport nødvendig?",
      answer: "Blokvognstransport bruges ofte, når maskiner eller tungt udstyr ikke kan transporteres med almindelig lastbil. Det kan være relevant ved gravemaskiner, skovmaskiner, specialudstyr eller andre større enheder. For at sikre korrekt rute, læsning og tilladelser er det en god ide at få transporten planlagt af en erfaren fagperson."
    },
    {
      question: "Kan man selv arrangere transport af mindre maskiner?",
      answer: "Mindre maskiner og udstyr kan i nogle tilfælde transporteres selv, hvis vægt, lastsikring og lovkrav er overholdt. Problemet opstår ofte, når udstyret er tungt, bredt, svært at læsse eller skal flyttes på tværs af længere afstande. Ved tvivl er det sikrest at få en vognmand til at vurdere, hvordan transporten kan udføres forsvarligt."
    },
    {
      question: "Hvad påvirker prisen på vognmandstransport?",
      answer: "Prisen afhænger blandt andet af afstand, vægt, volumen, adgangsforhold og om transporten kræver specialudstyr. Ved blokvognstransport kan ruteplanlægning, læsning, aflæsning og eventuelle tilladelser også have betydning. En konkret beskrivelse af opgaven gør det lettere at få en realistisk pris og undgå misforståelser."
    },
    {
      question: "Hvordan foregår udkørsel af træ?",
      answer: "Udkørsel af træ handler om at flytte fældet træ fra skov eller areal til et sted, hvor det kan afhentes eller viderebehandles. Opgaven kræver ofte maskiner, der kan arbejde i ujævnt terræn uden at skade området unødigt. En professionel løsning kan sikre, at træet håndteres effektivt, og at arealet efterlades mere ryddeligt."
    },
    {
      question: "Kan en vognmand hjælpe i både Nordjylland og Midtjylland?",
      answer: "Vognmandsopgaver kan ofte udføres på tværs af Jylland, afhængigt af opgavens type og det nødvendige materiel. Søgninger som vognmand Nordjylland, vognmand Midtjylland og vognmand Jylland dækker typisk over transportbehov i et større område. Ved større transportopgaver kan det være en fordel at samle flere kørsler eller koordinere dem med andre maskinelle opgaver."
    },
    {
      question: "Hvad skal man oplyse, når man skal bruge en vognmand?",
      answer: "Det vigtigste er at beskrive, hvad der skal transporteres, hvor det står, hvor det skal hen, og om der er særlige adgangsforhold. Oplys gerne mål, vægt, billeder og eventuelle krav til afhentning eller levering, hvis du har dem. Jo mere præcis informationen er, desto lettere er det at planlægge den rigtige bil, rute og pris."
    }
  ];

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-stone-900/40 border border-stone-900 p-8 sm:p-10 rounded-2xl space-y-6 mt-8">
      <div className="flex items-center gap-3 border-b border-stone-800 pb-4">
        <HelpCircle className="w-6 h-6 text-emerald-500 shrink-0" />
        <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-tight">
          Ofte stillede spørgsmål om vognmand
        </h2>
      </div>
      <div className="divide-y divide-stone-800/60">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <button
                onClick={() => toggleIndex(index)}
                className="w-full flex justify-between items-start gap-4 text-left text-white font-bold hover:text-emerald-400 transition-colors py-2 group"
                aria-expanded={isOpen}
              >
                <span className="text-sm sm:text-base tracking-tight leading-snug">
                  {item.question}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-stone-500 group-hover:text-emerald-400 shrink-0 transition-transform duration-300 mt-0.5 ${
                    isOpen ? 'rotate-180 text-emerald-500' : ''
                  }`} 
                />
              </button>
              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="text-stone-400 text-sm sm:text-base leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StubOgRodfraesningFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = [
    {
      question: "Hvad er forskellen på stubfræsning og rodfræsning?",
      answer: "Stubfræsning fjerner primært den synlige træstub efter fældning, så området bliver nemmere at bruge igen. Rodfræsning går typisk dybere eller bredere og kan være relevant, når rødder skaber problemer for ny beplantning, belægning eller jordarbejde. Den rigtige metode afhænger af stubben, rodnettet og hvad arealet skal bruges til bagefter."
    },
    {
      question: "Kan man selv fjerne en træstub?",
      answer: "Små stubbe kan nogle gange graves fri med håndkraft, hvis jorden er blød, og rødderne ikke er for kraftige. Større stubbe are ofte vanskelige at fjerne helt, og man risikerer at bruge meget tid uden at komme ordentligt i bund. Hvis stubben skal væk effektivt, kan stubfræsning være en mere sikker og skånsom løsning."
    },
    {
      question: "Hvornår giver rodfræsning mening?",
      answer: "Rodfræsning giver mening, når gamle rødder står i vejen for nyt græs, beplantning, adgangsveje eller andet arbejde på arealet. Det kan også være relevant efter træfældning, hvor rodnettet fortsat fylder eller gør jorden ujævn. En faglig vurdering kan afklare, om almindelig stubfræsning er nok, eller om der bør fræses mere grundigt."
    },
    {
      question: "Hvad koster stubfræsning typisk?",
      answer: "Prisen på stubfræsning afhænger især af stubbens størrelse, træsort, placering og adgangsforhold. Det kan også påvirke prisen, hvis der er mange stubbe, hårdt terræn eller behov for ekstra oprydning efter arbejdet. Den mest præcise pris fås normalt ved at sende billeder, mål og en kort beskrivelse af opgaven."
    },
    {
      question: "Kan stubfræsning udføres tæt på bygninger eller belægning?",
      answer: "Stubfræsning kan ofte udføres tæt på bygninger, hegn, fliser og andre faste kanter, men det kræver forsigtig vurdering. Maskinvalg og arbejdsmetode afhænger af pladsforhold, røddernes placering og risikoen for skader på omgivelserne. Ved trange forhold bør opgaven vurderes professionelt, før arbejdet sættes i gang."
    },
    {
      question: "Skal man vælge træfældning og stubfræsning sammen?",
      answer: "Det kan være praktisk at planlægge træfældning og stubfræsning samlet, hvis arealet skal bruges hurtigt bagefter. Når begge opgaver tænkes sammen, kan man ofte få en mere effektiv proces med mindre oprydning og færre arbejdsgange. Det er især relevant ved haver, læhegn, skovkanter og arealer, hvor der skal plantes nyt eller laves adgang."
    },
    {
      question: "Kan rodfræsning og stubfræsning udføres i hele Jylland?",
      answer: "Stubfræsning og rodfræsning kan ofte udføres i både Nordjylland, Midtjylland og andre dele af Jylland, hvis opgaven passer til materiellet. Ved større opgaver kan det give mening at samle flere stubbe eller arealer i samme kørsel. En kort beskrivelse af placering, antal stubbe og adgangsforhold gør det lettere at vurdere, om opgaven kan løses effektivt."
    }
  ];

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-stone-900/40 border border-stone-900 p-8 sm:p-10 rounded-2xl space-y-6 mt-8">
      <div className="flex items-center gap-3 border-b border-stone-800 pb-4">
        <HelpCircle className="w-6 h-6 text-emerald-500 shrink-0" />
        <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-tight">
          Ofte stillede spørgsmål om stub- og rodfræsning
        </h2>
      </div>
      <div className="divide-y divide-stone-800/60">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <button
                onClick={() => toggleIndex(index)}
                className="w-full flex justify-between items-start gap-4 text-left text-white font-bold hover:text-emerald-400 transition-colors py-2 group"
                aria-expanded={isOpen}
              >
                <span className="text-sm sm:text-base tracking-tight leading-snug">
                  {item.question}
                </span>
                <ChevronDown 
                  className={`w-5 h-5 text-stone-500 group-hover:text-emerald-400 shrink-0 transition-transform duration-300 mt-0.5 ${
                    isOpen ? 'rotate-180 text-emerald-500' : ''
                  }`} 
                />
              </button>
              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="text-stone-400 text-sm sm:text-base leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
